import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabase';
import {
  FamilyIncomeBand,
  FinancingType,
  ParentEducation
} from '../../types/domain';

export interface Profile {
  id: string;
  full_name: string;
  birth_date: string;
  diabetes_type: string;
  control_place: string;
  /** FK into control_places. The matching key since DF-09; control_place is a
   *  denormalized display copy of the chosen place's name. */
  hospital_id: string | null;
  role: string;
  /** FR-03 socio-economic data. Null until the parent fills the family-data
   *  screen; every profile created before FR-03 shipped starts that way. */
  parent_education: ParentEducation | null;
  family_income: FamilyIncomeBand | null;
  financing_type: FinancingType | null;
  parent_contact: string | null;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  /** True when no auth event arrived in time — a connectivity failure, not a
   *  signed-out user. Lets ProtectedRoute say so instead of bouncing to
   *  /login, which is what gets reported as "it logged me out". */
  authTimedOut: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const PROFILE_CACHE_PREFIX = 'profile_';
const PROFILE_CACHE_TTL_MS = 5 * 60 * 1000;

/** Per-user keys written by dataService. Kept in sync with dataService.ts. */
const CACHE_HISTORY_PREFIX = 'sb_cache_history_';
const CACHE_LAST_SITE_PREFIX = 'sb_cache_last_site_';
const CACHE_TARGETS_PREFIX = 'sb_cache_targets_';
const CACHE_CLINICAL_PREFIX = 'sb_cache_clinical_';

interface CachedProfileEnvelope {
  profile: Profile;
  cachedAt: number;
}

/**
 * Reads the offline profile cache.
 *
 * Backwards compatible with the pre-fix format (a bare Profile object), which
 * is reported as stale so the first load after this ships always revalidates.
 * Returns null when the cached entry belongs to a different user — the old
 * code never checked, which is a latent cross-user leak on a shared device.
 */
const readProfileCache = (
  userId: string
): { profile: Profile; isFresh: boolean } | null => {
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_PREFIX + userId);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);

    if (parsed && parsed.profile && typeof parsed.cachedAt === 'number') {
      const envelope = parsed as CachedProfileEnvelope;
      if (envelope.profile?.id !== userId) {
        return null;
      }
      return {
        profile: envelope.profile,
        isFresh: Date.now() - envelope.cachedAt < PROFILE_CACHE_TTL_MS
      };
    }

    // Legacy format: a bare Profile with no timestamp.
    if (parsed && parsed.id === userId) {
      return { profile: parsed as Profile, isFresh: false };
    }
    return null;
  } catch (err) {
    console.warn('Error reading cached profile:', err);
    return null;
  }
};

const writeProfileCache = (userId: string, profile: Profile) => {
  try {
    localStorage.setItem(
      PROFILE_CACHE_PREFIX + userId,
      JSON.stringify({ profile, cachedAt: Date.now() })
    );
  } catch {
    // Quota or private mode — the app works without the cache.
  }
};

/**
 * Clears the signing-out user's cached reads.
 *
 * Deliberately does NOT touch sb_offline_queue_<id>: that holds injections
 * which have not reached the server yet, and dropping it destroys patient
 * data. See docs/defects.md#df-02.
 */
const clearUserCaches = (userId: string) => {
  try {
    localStorage.removeItem(PROFILE_CACHE_PREFIX + userId);
    localStorage.removeItem(CACHE_HISTORY_PREFIX + userId);
    localStorage.removeItem(CACHE_LAST_SITE_PREFIX + userId);
    localStorage.removeItem(CACHE_TARGETS_PREFIX + userId);
    localStorage.removeItem(CACHE_CLINICAL_PREFIX + userId);
  } catch {
    // Nothing useful to do if storage is unavailable.
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [authTimedOut, setAuthTimedOut] = useState(false);
  // Which user the profile in state belongs to. Lets us tell "signing in as
  // someone new" (must show the spinner, or ProtectedRoute sees user-without-
  // profile and bounces to /complete-profile) from "the same session re-
  // announced" (must NOT flip loading, or the page remounts into 'Memuat...').
  const loadedProfileUserId = useRef<string | null>(null);

  const fetchProfile = async (userId: string): Promise<Profile | null | undefined> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Normal for a user who has not completed their profile yet.
          return null;
        }
        console.warn('Error fetching profile:', error);
        return undefined;
      }

      writeProfileCache(userId, data as Profile);
      return data as Profile;
    } catch (error) {
      console.warn('Error fetching profile:', error);
      return undefined;
    }
  };

  const refreshProfile = async () => {
    const currentUser = user || (await supabase.auth.getUser()).data.user;
    if (currentUser) {
      const profileData = await fetchProfile(currentUser.id);
      if (profileData !== undefined) {
        setProfile(profileData);
      }
    }
  };

  useEffect(() => {
    let mounted = true;

    /**
     * Stale-while-revalidate (DF-06, and DF-02 candidate (b)).
     *
     * The cache paints immediately and is also the offline fallback, so it is
     * revalidated rather than removed. A failed read keeps whatever we had.
     */
    const hydrateProfile = async (userId: string, force = false) => {
      const cached = readProfileCache(userId);

      if (cached) {
        if (mounted) {
          setProfile(cached.profile);
          loadedProfileUserId.current = userId;
          setLoading(false);
        }
        if (cached.isFresh && !force) {
          return;
        }
      }

      const fetched = await fetchProfile(userId);
      if (!mounted) {
        return;
      }
      // undefined means the read failed — keep the cached profile.
      if (fetched !== undefined) {
        setProfile(fetched);
        loadedProfileUserId.current = userId;
      }
      setLoading(false);
    };

    const handleSession = (nextSession: Session | null, force = false) => {
      setSession(nextSession);
      setUser(nextSession?.user ?? null);

      if (!nextSession?.user) {
        setProfile(null);
        loadedProfileUserId.current = null;
        setLoading(false);
        return;
      }

      const nextUserId = nextSession.user.id;
      if (loadedProfileUserId.current !== nextUserId) {
        setLoading(true);
      }
      // Deferred out of the auth callback on purpose: awaiting a Supabase call
      // inside onAuthStateChange blocks on the auth client's internal lock and
      // can hang until it times out, which surfaces as a bounce to /login.
      setTimeout(() => {
        if (mounted) {
          void hydrateProfile(nextUserId, force);
        }
      }, 0);
    };

    // Subscribe before reading the session: the previous code kicked off
    // getSession() first, so an auth event arriving during that round trip was
    // dropped. INITIAL_SESSION covers the initial read in supabase-js v2.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!mounted) {
        return;
      }
      setAuthTimedOut(false);

      if (event === 'TOKEN_REFRESHED') {
        // Session only — no profile work, and no loading flap.
        setSession(nextSession);
        setUser(nextSession?.user ?? null);
        return;
      }

      if (event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
        setProfile(null);
        loadedProfileUserId.current = null;
        setLoading(false);
        return;
      }

      // INITIAL_SESSION, SIGNED_IN, USER_UPDATED. USER_UPDATED forces a read so
      // a role or profile change is picked up without signing out.
      handleSession(nextSession, event === 'USER_UPDATED');
    });

    // If no auth event ever arrives we report an honest failure rather than
    // rendering the login screen, which is what users describe as being
    // silently logged out.
    const safetyTimeout = setTimeout(() => {
      if (mounted) {
        setAuthTimedOut(true);
        setLoading(false);
      }
    }, 12000);

    return () => {
      mounted = false;
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    const userId = user?.id;
    loadedProfileUserId.current = null;
    await supabase.auth.signOut();
    if (userId) {
      clearUserCaches(userId);
    }
    setProfile(null);
    setUser(null);
    setSession(null);
  };

  return (
    <AuthContext.Provider value={{ session, user, profile, loading, authTimedOut, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
